<template>
    <!-- Модальное окно "Введите пин-код для продолжения работы" -->
    <a-modal
        v-model:visible="isSessionModalVisible"
        :footer="null"
        :mask-closable="false"
        :closable="false"
        centered
    >
        <session-pin-in
            v-if="isSessionModalVisible"
            @success="onPinInEmitSuccess"
            @no-access="onPinInEmitNoAccess"
            @forgot-pin="onPinInEmitForgotPin"
            @expired="onPinInExpired"
        />
    </a-modal>

    <!-- Модальное окно "Введите логин и пароль для продолжения работы" -->
    <a-modal
        v-model:visible="isSessionModalSignInVisible"
        :footer="null"
        :mask-closable="false"
        :closable="false"
        centered
    >
        <session-sign-in
            v-if="isSessionModalSignInVisible"
            @success="onPinInEmitSuccess"
            @no-access="onPinInEmitNoAccess"
            @forgot-pin="onPinInEmitForgotPin"
        />
    </a-modal>

    <!-- Модальное окно "Доступ заблокирован" -->
    <a-modal
        v-model:visible="isBlockedModalVisible"
        :footer="null"
        :mask-closable="false"
        :closable="false"
        centered
    >
        <session-no-access
            v-if="isBlockedModalVisible"
            @button-ok="onNoAccessEmitButtonOk"
        />
    </a-modal>

    <!-- Модальное окно "Восстановление пин-кода" -->
    <a-modal
        v-model:visible="isForgotPinModalVisible"
        :footer="null"
        :mask-closable="false"
        :closable="false"
        centered
    >
        <session-forgot-pin
            v-if="isForgotPinModalVisible"
            @button-ok="onForgotPinEmitButtonOk"
        />
    </a-modal>
</template>

<script>
import { RouterConfig } from '@/utils';
import SessionPinIn from '@/components/session/PinIn.vue';
import SessionNoAccess from '@/components/session/NoAccess.vue';
import SessionForgotPin from '@/components/session/ForgotPin.vue';
import SessionSignIn from '@/components/session/SignIn.vue';
import sseService from '@/utils/sse';

export default {
    name: 'SessionController',
    components: {
        SessionPinIn,
        SessionNoAccess,
        SessionForgotPin,
        SessionSignIn,
    },
    data() {
        return {
            isSessionModalVisible: false,
            isBlockedModalVisible: false,
            isForgotPinModalVisible: false,
            isSessionModalSignInVisible: false,
            state: {
                timer: null,
                isSessionExpired: false,
                attempts: 0,
                maxAttempts: 5,
            },
            config: {
                // Время сессии в миллисекундах из .env (по умолчанию 30 минут)
                sessionDuration: (
                    process.env.VUE_APP_SESSION_DURATION_MINUTES
                        ? Number(process.env.VUE_APP_SESSION_DURATION_MINUTES)
                        : 30
                ) * 60 * 1000,
                events: ['click', 'keydown'],
                onSessionExpired: () => {
                    localStorage.removeItem('LoggedIn');
                    this.controllerSession();
                },
                onMaxAttemptsReached: () => {
                    this.closeSessionModal();
                    this.openBlockedModal();
                },
            },
        };
    },
    watch: {
        // Наблюдаем за изменением текущего маршрута
        '$route.name': {
            handler(newName) {
                const internalOnlyRoutes = [
                    RouterConfig.loginPinIn.name,
                    RouterConfig.loginSignIn.name,
                ];

                if (internalOnlyRoutes.includes(newName)) {
                    this.closeAllModal();
                }
            },
        },
    },
    mounted() {
        this.checkAuthStatus();

        // Отслеживаем изменения в localStorage.LoggedIn
        window.addEventListener('storage', this.handleStorageChange);
    },
    beforeUnmount() {
        // Удаляем обработчики событий
        this.config.events.forEach((event) => {
            window.removeEventListener(event, this.handleUserActivity);
        });

        window.removeEventListener('storage', this.handleStorageChange);

        if (this.state.timer) {
            clearTimeout(this.state.timer);
        }
    },
    methods: {
        async updateParametrs() {
            try {
                const response = await this.$http.get('/v2/auth/settings');

                localStorage.parametrs = JSON.stringify(response.data);

                return response.data || {};
            } catch (error) {
                console.error('[Controller]:', error);
                return {};
            }
        },
        checkAuthStatus() {
            // Всегда удаляем старые обработчики
            this.config.events.forEach((event) => {
                window.removeEventListener(event, this.handleUserActivity);
            });

            if (localStorage.getItem('LoggedIn') === 'true') {
                // Запускаем таймер
                this.startTimer();

                // Устанавливаем обработчики событий
                this.config.events.forEach((event) => {
                    window.addEventListener(event, this.handleUserActivity, { passive: true });
                });
            } else {
                // Если пользователь вышел — останавливаем таймер
                this.stopTimer();
            }
        },
        startTimer() {
            this.state.timer = setTimeout(() => {
                this.handleSessionExpired();
            }, this.config.sessionDuration);
        },
        resetTimer() {
            if (this.state.timer) {
                clearTimeout(this.state.timer);
            }

            this.startTimer();

            // Синхронизация через localStorage
            localStorage.setItem('sessionLastActivity', Date.now().toString());
        },
        stopTimer() {
            if (this.state.timer) {
                clearTimeout(this.state.timer);
                this.state.timer = null;
            }
        },
        handleStorageChange(event) {
            if (event.key === 'sessionLastActivity') {
                this.resetTimer();
            } else if (event.key === 'LoggedIn') {
                // Если изменился статус авторизации — перепроверяем
                this.checkAuthStatus();
            } else if (event.key === 'sessionExpired') {
                // Другая вкладка истекла — открываем модальное окно и останавливаем таймер
                this.stopTimer();
                this.controllerSession();
            } else if (event.key === 'sessionModalClosed') {
                // Другая вкладка успешно ввела пин-код — закрываем модальное окно
                this.closeAllModal();
            }
        },
        handleSessionExpired() {
            this.state.isSessionExpired = true;
            this.config.onSessionExpired();

            // Отправляем сигнал другим вкладкам
            localStorage.setItem('sessionExpired', Date.now().toString());
        },
        handleUserActivity() {
            if (localStorage.getItem('LoggedIn') === 'true') {
                this.resetTimer(); // <-- Обнуляем таймер при каждом действии
            }
        },
        closeAllModal() {
            this.isSessionModalVisible = false;
            this.isBlockedModalVisible = false;
            this.isForgotPinModalVisible = false;
            this.isSessionModalSignInVisible = false;
        },
        // Методы открытия модальных окон
        openSessionModal() {
            if (this.isSessionModalVisible || this.isSessionModalSignInVisible) {
                return;
            }
            this.closeAllModal();

            const internalOnlyRoutes = [
                RouterConfig.loginPinIn.name,
                RouterConfig.loginSignIn.name,
            ];
            const toName = this.$router.currentRoute.value.name || '';

            if (internalOnlyRoutes.includes(toName)) {
                return;
            }

            if ('parametrs' in localStorage && Object.keys(localStorage.parametrs).length) {
                const {
                    pinGlobalSetup,
                    pinSetup,
                } = JSON.parse(localStorage.parametrs);

                if (pinGlobalSetup && pinSetup) {
                    this.isSessionModalVisible = true;
                } else {
                    this.isSessionModalSignInVisible = true;
                }
            } else {
                this.isSessionModalSignInVisible = true;
            }
        },
        closeSessionModal() {
            this.isSessionModalVisible = false;
            this.isSessionModalSignInVisible = false;
        },
        openBlockedModal() {
            this.closeAllModal();
            this.isBlockedModalVisible = true;
        },
        openForgotPinModal() {
            this.closeAllModal();
            this.isForgotPinModalVisible = true;
        },
        // onPinInEmitSuccess() {
        //     this.closeAllModal();
        //     localStorage.LoggedIn = true;
        //     this.resetTimer();
        // },
        onPinInEmitSuccess() {
            localStorage.setItem('LoggedIn', 'true');
            this.closeAllModal();
            this.resetTimer();

            // Восстанавливаем SSE соединение после успешного подтверждения сессии
            if (sseService.getStatus() === 'disconnected') {
                sseService.connect();
            }

            // Отправляем сигнал другим вкладкам закрыть модальное окно
            localStorage.setItem('sessionModalClosed', Date.now().toString());
            localStorage.setItem('sessionLastActivity', Date.now().toString());
        },
        onPinInEmitNoAccess() {
            this.closeAllModal();
            this.openBlockedModal();
        },
        onPinInEmitForgotPin() {
            this.closeAllModal();
            this.openForgotPinModal();
        },
        onNoAccessEmitButtonOk() {
            this.closeAllModal();
            this.$router.push({ name: RouterConfig.login.name });
        },
        onForgotPinEmitButtonOk() {
            this.closeAllModal();
            this.$router.push({ name: RouterConfig.login.name });
        },
        onPinInExpired() {
            this.stopTimer();
            this.state.isSessionExpired = false;
            this.closeAllModal();
            localStorage.removeItem('LoggedIn');
            localStorage.setItem('sessionModalClosed', Date.now().toString());
        },
        async logout() {
            try {
                // Закрываем SSE соединение перед logout
                sseService.disconnect();
                // TODO change url request
                await this.$http.post('/user/logout');
            } catch (error) {
                // Error handling
                console.error('[logout]:', error);
            }
        },
        async controllerSession() {
            try {
                await this.updateParametrs();
                await this.logout();
            } catch (error) {
                // Error handling
                console.error('[controllerSession]:', error);
            } finally {
                this.openSessionModal();
            }
        },
    },
};
</script>
